import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, BedDouble, Camera, Car, Compass, Heart, Hotel, Landmark, Mountain, Sparkles, Users, Utensils, Waves, Tent, Flower2, ShieldCheck, BookOpen } from 'lucide-react';
import { BACKEND_URL } from '../../config';
import SEO from '../../components/SEO';
import LeadOffer from '../../components/site/LeadOffer';
import { SitePage, PageHero, Facts, SectionHead, PackageCard, CardSkeletons, CtaBand } from '../../components/site/ui';
import { inr, toNumber, unsplash, waLink } from '../../components/site/brand';
import { DESTINATIONS } from '../../components/site/destinations';

const THEMES = {
  honeymoon: {
    title: 'Honeymoon', accent: 'packages', path: '/honeymoon-packages',
    kicker: 'For two', hero: 'photo-1615966650071-855b15f29ad1',
    lede: 'Overwater villas, private dinners, couple spas and slow mornings — honeymoons planned around the two of you.',
    match: /honeymoon|romance|romantic|couple/i,
    features: [[Heart, 'Romantic touches', 'Bed decoration, cake and candlelight dinner on request'], [BedDouble, 'Handpicked stays', 'Pool villas, water villas and honeymoon suites'], [Sparkles, 'Private experiences', 'Sunset cruises, couple spa and photo shoots'], [ShieldCheck, 'Stress-free', 'Visas, transfers and on-trip help handled']],
  },
  family: {
    title: 'Family', accent: 'holidays', path: '/family-holiday-packages',
    kicker: 'All ages welcome', hero: 'photo-1602216056096-3b40cc0c9944',
    lede: 'Theme parks, easy pacing, family rooms and food everyone will eat — holidays that work for kids and grandparents alike.',
    match: /family|fun|kids/i,
    features: [[Users, 'Kid-friendly plans', 'Theme parks, beaches and short drives'], [Hotel, 'Family rooms', 'Connecting rooms and extra beds arranged'], [Utensils, 'Indian meals', 'Veg and Jain food options where possible'], [Car, 'Private transport', 'Comfortable vehicles throughout']],
  },
  adventure: {
    title: 'Adventure', accent: 'tours', path: '/adventure-tour-packages',
    kicker: 'For the bold', hero: 'photo-1593118845043-359e5f628214',
    lede: 'Ladakh bike expeditions, Himalayan treks, scuba in the Andamans and desert safaris — adventure with safety built in.',
    match: /adventure|trek|expedition|bike|safari|scuba|base camp|circuit|photography/i,
    features: [[Mountain, 'Treks & expeditions', 'Graded routes with acclimatisation'], [Compass, 'Bike trips', 'Royal Enfield tours with backup vehicle'], [Waves, 'Water sports', 'Scuba, rafting and snorkelling'], [Tent, 'Camps', 'Desert, lakeside and mountain camps']],
  },
  pilgrimage: {
    title: 'Pilgrimage', accent: 'tours', path: '/pilgrimage-tour-packages',
    kicker: 'Spiritual journeys from Vijayawada', hero: 'photo-1709389137241-af48d39f8b46',
    lede: 'Tirupati, Srisailam, Kashi–Ayodhya, Arunachalam, Rameswaram, Sabarimala and Char Dham — darshan, stays and travel arranged so you can focus on the yatra.',
    match: /char dham|yatra|pilgrim|darshan|temple|spiritual/i,
    usePilgrimages: true,
    // Most enquiries come from Andhra Pradesh: show the yatras people here ask for first
    priority: [/tirupati|tirumala/i, /srisailam/i, /pancharama/i, /kashi|ayodhya|varanasi|prayag/i, /arunachal|kanchi/i, /rameswaram|madurai/i, /yadagiri|bhadrachal/i, /sabarimala/i, /kollur|udupi|mookambika/i, /shirdi/i, /char dham|kedarnath|badrinath/i],
    quick: ['Tirupati', 'Srisailam', 'Kashi & Ayodhya', 'Arunachalam', 'Rameswaram', 'Sabarimala', 'Char Dham'],
    features: [[Flower2, 'Darshan planning', 'Timings and queues planned in advance'], [Hotel, 'Stays near temples', 'Clean, comfortable hotels close by'], [Car, 'Door-to-door travel', 'Helicopter options for Char Dham'], [Users, 'Senior-friendly', 'Easy pacing, ponies and palkis arranged']],
  },
  luxury: {
    title: 'Luxury', accent: 'holidays', path: '/luxury-holiday-packages',
    kicker: '5-star journeys', hero: 'photo-1688949078626-a358f500e063',
    lede: 'Palace hotels, water villas, private pool villas and chauffeured cars — 5-star holidays in India and abroad, planned to the last detail.',
    match: /luxury/i, matchClass: /5★/,
    features: [[Hotel, '5-star stays', 'Palace hotels, water villas and pool villas'], [Car, 'Private chauffeur', 'Premium cars and private transfers throughout'], [Sparkles, 'Signature experiences', 'Private guides, yacht cruises, spa and fine dining'], [ShieldCheck, 'One point of contact', 'A dedicated VK Tours manager through the trip']],
  },
  heritage: {
    title: 'Heritage', accent: 'tours', path: '/heritage-tour-packages',
    kicker: 'History & culture', hero: 'photo-1477587458883-47145ed94245',
    lede: 'Forts, palaces, pyramids and old cities — with expert local guides and heritage hotels along the way.',
    match: /heritage|palace|cultur|golden triangle|monastery|pyramid|nile|golden ring|cairo|petra|grand tour/i,
    features: [[Landmark, 'Monuments', 'Forts, palaces and UNESCO sites'], [BookOpen, 'Expert guides', 'Licensed local historians'], [Hotel, 'Heritage stays', 'Palace and haveli hotels'], [Camera, 'Culture', 'Food walks, crafts and performances']],
  },
};

// The live database holds wrong photos for some pilgrimages (e.g. the Taj Mahal on Dwarka, Mathura and Shirdi).
// These overrides match the picture to the place, by package name.
const PILGRIMAGE_IMAGES = {
  'dwarka somnath yatra': 'photo-1717326630799-703fe906e283',
  'mathura vrindavan yatra': 'photo-1657089975754-c93bde43b01e',
  'shirdi sai baba darshan': 'photo-1629640890590-7836d69c5237',
  'tirupati balaji darshan': 'photo-1709389137241-af48d39f8b46',
};
const pilgrimageImage = (name, fallback) => (PILGRIMAGE_IMAGES[String(name || '').trim().toLowerCase()] ? unsplash(PILGRIMAGE_IMAGES[String(name).trim().toLowerCase()], 1200) : fallback);

const ThemeToursTemplate = ({ theme }) => {
  const navigate = useNavigate();
  const t = THEMES[theme] || THEMES.family;
  const [packages, setPackages] = useState([]);
  const [pilgrimages, setPilgrimages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const calls = [axios.get(`${BACKEND_URL}/api/packages`)];
    if (t.usePilgrimages) calls.push(axios.get(`${BACKEND_URL}/api/pilgrimages`));
    Promise.allSettled(calls).then(([a, b]) => {
      if (!alive) return;
      const all = a.status === 'fulfilled' && Array.isArray(a.value.data) ? a.value.data : [];
      setPackages(all.filter((p) => t.match.test(p.title || '') || (t.matchClass && t.matchClass.test(p.hotelClass || '')) || (Array.isArray(p.theme_tags) && p.theme_tags.includes(theme))));
      setPilgrimages(b && b.status === 'fulfilled' && Array.isArray(b.value.data) ? b.value.data : []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [theme, t]);

  // Pilgrimage-only trips (no detail page): show them as quote-on-request cards
  const extra = useMemo(() => {
    const titles = new Set(packages.map((p) => (p.title || '').toLowerCase()));
    return pilgrimages
      .filter((p) => !titles.has((p.name || '').toLowerCase()))
      .map((p) => ({ id: `pil-${p.id}`, title: p.name, duration: p.duration, price: p.price, originalPrice: p.originalPrice, rating: p.rating, image: pilgrimageImage(p.name, p.image), destination: Array.isArray(p.temples) ? p.temples[0] : '', _pilgrimage: true }));
  }, [pilgrimages, packages]);

  const rank = (p) => {
    if (!t.priority) return 0;
    const name = `${p.title || ''} ${p.destination || ''}`;
    const i = t.priority.findIndex((re) => re.test(name));
    return i === -1 ? t.priority.length : i;
  };
  const cards = [...packages, ...extra].map((p, i) => ({ p, i })).sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i).map(({ p }) => p);
  const [showAll, setShowAll] = useState(false);
  const FIRST = 9;
  const shown = showAll ? cards : cards.slice(0, FIRST);
  const prices = cards.map((p) => toNumber(p.price)).filter(Boolean);
  const from = prices.length ? Math.min(...prices) : null;
  const destCount = new Set(packages.map((p) => (p.destination || '').toLowerCase())).size;
  const msg = `Hi VK Tours, I'm looking for ${t.title.toLowerCase()} ${t.accent}. Please share options.`;

  const open = (p) => {
    if (p._pilgrimage) window.open(waLink(`Hi VK Tours, please share details and price for "${p.title}" (${p.duration}).`), '_blank', 'noopener');
    else navigate(`/package/${p.id}`);
  };

  return (
    <SitePage>
      <SEO title={`${t.title} ${t.accent[0].toUpperCase()}${t.accent.slice(1)}`} description={t.lede} path={t.path} image={unsplash(t.hero, 1200)} />
      <PageHero
        image={unsplash(t.hero, 2000)}
        imageAlt={`${t.title} ${t.accent}`}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Holiday styles' }, { label: `${t.title} ${t.accent}` }]}
        kicker={t.kicker}
        title={t.title}
        accent={t.accent}
        lede={t.lede}
      >
        <a className="vk-btn vk-btn--hot" href="#packages">{loading ? 'See trips' : `See ${cards.length} ${cards.length === 1 ? 'trip' : 'trips'}`} <ArrowRight size={18} /></a>
        <a className="vk-btn vk-btn--ghost" href={waLink(msg)} target="_blank" rel="noopener noreferrer">Plan a custom trip</a>
      </PageHero>

      <Facts items={[
        { value: loading ? '…' : String(cards.length), label: 'Trips available' },
        { value: loading ? '…' : String(destCount || '—'), label: 'Destinations' },
        { value: from ? inr(from) : 'On request', label: 'Starting from' },
        { value: '24×7', label: 'On-trip support' },
      ]} />

      {t.quick ? (
        <section className="vk-section" style={{ paddingTop: 28, paddingBottom: 8 }}>
          <div className="vk-wrap">
            <p className="vk-kicker">Popular yatras · tap to ask on WhatsApp</p>
            <ul className="vk-chips">
              {t.quick.map((q) => (
                <li key={q}><a className="vk-chip" href={waLink(`Hi VK Tours, please share ${q} yatra packages and prices from Vijayawada.`)} target="_blank" rel="noopener noreferrer">{q}</a></li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <section className="vk-section vk-section--sand" id="packages" style={{ scrollMarginTop: 'calc(var(--header-h) + 12px)' }}>
        <div className="vk-wrap">
          <SectionHead kicker="Ready to book" title={`${t.title} ${t.accent}`} />
          {loading ? <CardSkeletons count={4} /> : cards.length ? (
            <div className="vk-grid">
              {shown.map((p) => <PackageCard key={p.id} pkg={p} placeLabel={DESTINATIONS[(p.destination || '').toLowerCase()]?.name || p.destination} onOpen={open} />)}
            </div>
          ) : null}
          {!loading && cards.length > FIRST && !showAll ? (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button type="button" className="vk-btn vk-btn--outline" onClick={() => setShowAll(true)}>Show all {cards.length} trips</button>
            </div>
          ) : null}
          {!loading && !cards.length ? (
            <div className="vk-empty">
              <h3>We’ll design it for you</h3>
              <p>Tell us your dates and budget and we’ll send a tailor-made {t.title.toLowerCase()} plan.</p>
              <a className="vk-btn vk-btn--hot" href={waLink(msg)} target="_blank" rel="noopener noreferrer">Get a quote on WhatsApp</a>
            </div>
          ) : null}
        </div>
      </section>

      <LeadOffer destination={theme === 'honeymoon' ? 'Honeymoon' : theme === 'pilgrimage' ? 'Pilgrimage' : ''} pageLabel={`${theme} packages`} />

      <section className="vk-section">
        <div className="vk-wrap">
          <SectionHead kicker="What’s included in the experience" title={`Why book ${t.title.toLowerCase()} trips with us`} />
          <div className="vk-feats">
            {t.features.map(([Icon, title, text]) => (
              <div key={title} className="vk-feat"><Icon size={28} /><h3>{title}</h3><p>{text}</p></div>
            ))}
          </div>
        </div>
      </section>


      <CtaBand message={msg} />
    </SitePage>
  );
};

export default ThemeToursTemplate;
